"use strict";
/**
 * Word stems as the server index builds them, so a local search for
 * "Häuser" finds "Haus" like the server does. The server folds letters to
 * their base form (ä → a, ß → s) and runs `morphology='stem_en,
 * libstemmer_de'`: the Snowball English stemmer first, the Snowball German
 * stemmer only when the English one left the word unchanged.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.foldSearchWord = foldSearchWord;
exports.stemSearchWord = stemSearchWord;
exports.englishStem = englishStem;
exports.germanStem = germanStem;
/** Letters the server maps to another base letter than their decomposition gives. */
const SERVER_LETTERS = {
    ß: 's',
    œ: 'o',
    ø: 'o',
    đ: 'd',
    ł: 'l',
    ı: 'i',
    ŀ: 'l',
    ħ: 'h',
    ŧ: 't',
};
/** The server keeps at most this many characters of a word. */
const SERVER_MAX_WORD_LENGTH = 42;
/**
 * Base letters without accents, lower-cased and cut like the server's
 * charset table: "Reißzwecke" → "reiszwecke", "Cœur" → "cour", "ＡＢＣ" → "abc".
 */
function foldSearchWord(word) {
    let folded = '';
    for (const char of word.normalize('NFKC').toLowerCase()) {
        folded +=
            SERVER_LETTERS[char] ?? char.normalize('NFD').replace(/\p{M}/gu, '');
    }
    return [...folded].slice(0, SERVER_MAX_WORD_LENGTH).join('');
}
const LATIN_WORD = /^[a-z0-9]*[a-z][a-z0-9]*$/;
/** Server stem of one word, or `null` for words the stemmers skip (CJK, emoji). */
function stemSearchWord(word) {
    const folded = foldSearchWord(word);
    if (!LATIN_WORD.test(folded))
        return null;
    const english = englishStem(folded);
    return english === folded ? germanStem(folded) : english;
}
const VOWELS = 'aeiouy';
function isVowel(c) {
    return c !== undefined && VOWELS.includes(c);
}
/** Start of the region after the first non-vowel that follows a vowel. */
function regionStart(word, from) {
    for (let i = from + 1; i < word.length; i++) {
        if (!isVowel(word[i]) && isVowel(word[i - 1]))
            return i + 1;
    }
    return word.length;
}
function longestSuffix(word, suffixes) {
    let found = '';
    for (const suffix of suffixes) {
        if (word.endsWith(suffix) && suffix.length > found.length)
            found = suffix;
    }
    return found;
}
// ---------------------------------------------------------------------------
// The server's stem_en: Snowball English (Porter2) in its early edition,
// checked word by word against CALL KEYWORDS: only "gener" has a fixed R1
// (not "commun"/"arsen"), step 1a drops a final "s" without looking for a
// vowel, step 3 drops "ative" anywhere in R1 and step 5 removes "e" and
// then a double "l" in one pass.
// ---------------------------------------------------------------------------
const ENGLISH_EXCEPTIONS = {
    skis: 'ski',
    skies: 'sky',
    dying: 'die',
    lying: 'lie',
    tying: 'tie',
    idly: 'idl',
    gently: 'gentl',
    ugly: 'ugli',
    early: 'earli',
    only: 'onli',
    singly: 'singl',
    sky: 'sky',
    news: 'news',
    howe: 'howe',
    atlas: 'atlas',
    cosmos: 'cosmos',
    bias: 'bias',
    andes: 'andes',
};
const ENGLISH_KEEP_AFTER_1A = new Set([
    'inning',
    'outing',
    'canning',
    'herring',
    'earring',
    'proceed',
    'exceed',
    'succeed',
]);
const DOUBLES = ['bb', 'dd', 'ff', 'gg', 'mm', 'nn', 'pp', 'rr', 'tt'];
/**
 * Vowel, then a non-vowel other than w, x or Y, preceded by a non-vowel;
 * or a vowel and a non-vowel as the whole word.
 */
function endsShortSyllable(word) {
    const n = word.length;
    if (n === 2)
        return isVowel(word[0]) && !isVowel(word[1]);
    return (n >= 3 &&
        !isVowel(word[n - 3]) &&
        isVowel(word[n - 2]) &&
        !isVowel(word[n - 1]) &&
        !'wxY'.includes(word[n - 1]));
}
const ENGLISH_STEP2 = {
    tional: 'tion',
    enci: 'ence',
    anci: 'ance',
    abli: 'able',
    entli: 'ent',
    izer: 'ize',
    ization: 'ize',
    ational: 'ate',
    ation: 'ate',
    ator: 'ate',
    alism: 'al',
    aliti: 'al',
    alli: 'al',
    fulness: 'ful',
    ousli: 'ous',
    ousness: 'ous',
    iveness: 'ive',
    iviti: 'ive',
    biliti: 'ble',
    bli: 'ble',
    ogi: 'og',
    fulli: 'ful',
    lessli: 'less',
    li: '',
};
const ENGLISH_STEP3 = {
    tional: 'tion',
    ational: 'ate',
    alize: 'al',
    icate: 'ic',
    iciti: 'ic',
    ical: 'ic',
    ful: '',
    ness: '',
    ative: '',
};
const ENGLISH_STEP4 = [
    'al',
    'ance',
    'ence',
    'er',
    'ic',
    'able',
    'ible',
    'ant',
    'ement',
    'ment',
    'ent',
    'ism',
    'ate',
    'iti',
    'ous',
    'ive',
    'ize',
    'ion',
];
function englishStem(input) {
    if (input.length <= 2)
        return input;
    const exception = ENGLISH_EXCEPTIONS[input];
    if (exception)
        return exception;
    // "y" at the start or after a vowel counts as a consonant.
    let word = [...input]
        .map((c, i, all) => c === 'y' && (i === 0 || isVowel(all[i - 1])) ? 'Y' : c)
        .join('');
    const r1 = word.startsWith('gener') ? 5 : regionStart(word, 0);
    const r2 = regionStart(word, r1);
    const inR1 = (suffix) => word.length - suffix.length >= r1;
    const inR2 = (suffix) => word.length - suffix.length >= r2;
    const cut = (suffix, replacement = '') => {
        word = word.slice(0, word.length - suffix.length) + replacement;
    };
    // Step 1a
    {
        const suffix = longestSuffix(word, ['sses', 'ied', 'ies', 's', 'us', 'ss']);
        if (suffix === 'sses')
            cut(suffix, 'ss');
        else if (suffix === 'ied' || suffix === 'ies') {
            cut(suffix, word.length - suffix.length > 1 ? 'i' : 'ie');
        }
        else if (suffix === 's') {
            cut(suffix);
        }
    }
    if (ENGLISH_KEEP_AFTER_1A.has(word))
        return word;
    // Step 1b
    {
        const suffix = longestSuffix(word, [
            'eed',
            'eedly',
            'ed',
            'edly',
            'ing',
            'ingly',
        ]);
        if (suffix === 'eed' || suffix === 'eedly') {
            if (inR1(suffix))
                cut(suffix, 'ee');
        }
        else if (suffix && [...word.slice(0, -suffix.length)].some(isVowel)) {
            cut(suffix);
            if (/(at|bl|iz)$/.test(word))
                word += 'e';
            else if (DOUBLES.some(pair => word.endsWith(pair))) {
                word = word.slice(0, -1);
            }
            else if (r1 >= word.length && endsShortSyllable(word))
                word += 'e';
        }
    }
    // Step 1c
    if (word.length > 2 && /[yY]$/.test(word) && !isVowel(word.at(-2))) {
        cut('y', 'i');
    }
    // Step 2
    {
        const suffix = longestSuffix(word, Object.keys(ENGLISH_STEP2));
        const before = word[word.length - suffix.length - 1];
        if (suffix &&
            inR1(suffix) &&
            (suffix !== 'ogi' || before === 'l') &&
            (suffix !== 'li' || 'cdeghkmnrt'.includes(before ?? ' '))) {
            cut(suffix, ENGLISH_STEP2[suffix]);
        }
    }
    // Step 3
    {
        const suffix = longestSuffix(word, Object.keys(ENGLISH_STEP3));
        if (suffix && inR1(suffix))
            cut(suffix, ENGLISH_STEP3[suffix]);
    }
    // Step 4
    {
        const suffix = longestSuffix(word, ENGLISH_STEP4);
        if (suffix &&
            inR2(suffix) &&
            (suffix !== 'ion' || /[st]$/.test(word.slice(0, -3)))) {
            cut(suffix);
        }
    }
    // Step 5: "e", then a double "l", like Porter's 5a and 5b.
    if (word.endsWith('e') &&
        (inR2('e') || (inR1('e') && !endsShortSyllable(word.slice(0, -1))))) {
        cut('e');
    }
    if (word.endsWith('ll') && inR2('l'))
        cut('l');
    return word.replace(/Y/g, 'y');
}
// ---------------------------------------------------------------------------
// Snowball German, the server's libstemmer_de, on already folded words.
// ---------------------------------------------------------------------------
function germanStem(input) {
    // "u" and "y" between vowels count as consonants.
    let word = input.replace(/ß/g, 'ss');
    const marked = [...word];
    for (let i = 1; i < marked.length - 1; i++) {
        if ((marked[i] === 'u' || marked[i] === 'y') &&
            isVowel(marked[i - 1]) &&
            isVowel(marked[i + 1])) {
            marked[i] = marked[i].toUpperCase();
        }
    }
    word = marked.join('');
    const standardR1 = regionStart(word, 0);
    const r1 = Math.max(3, standardR1);
    const r2 = regionStart(word, standardR1);
    const inR1 = (suffix) => word.length - suffix.length >= r1;
    const inR2 = (suffix) => word.length - suffix.length >= r2;
    // Step 1
    {
        const a = longestSuffix(word, ['em', 'ern', 'er']);
        const b = longestSuffix(word, ['e', 'en', 'es']);
        const suffix = a.length >= b.length ? a : b;
        if (suffix) {
            if (inR1(suffix)) {
                word = word.slice(0, -suffix.length);
                if (b === suffix && word.endsWith('niss'))
                    word = word.slice(0, -1);
            }
        }
        else if (word.endsWith('s') &&
            inR1('s') &&
            'bdfghklmnrt'.includes(word.at(-2) ?? '')) {
            word = word.slice(0, -1);
        }
    }
    // Step 2
    {
        const suffix = longestSuffix(word, ['en', 'er', 'est']);
        if (suffix) {
            if (inR1(suffix))
                word = word.slice(0, -suffix.length);
        }
        else if (word.endsWith('st') &&
            inR1('st') &&
            word.length - 2 >= 4 &&
            'bdfghklmnt'.includes(word.at(-3) ?? '')) {
            word = word.slice(0, -2);
        }
    }
    // Step 3
    {
        const suffix = longestSuffix(word, [
            'end',
            'ung',
            'ig',
            'ik',
            'isch',
            'lich',
            'heit',
            'keit',
        ]);
        if (suffix && inR2(suffix)) {
            const stem = word.slice(0, -suffix.length);
            if (suffix === 'end' || suffix === 'ung') {
                word = stem;
                if (word.endsWith('ig') && inR2('ig') && !word.endsWith('eig')) {
                    word = word.slice(0, -2);
                }
            }
            else if (suffix === 'ig' || suffix === 'ik' || suffix === 'isch') {
                if (!stem.endsWith('e'))
                    word = stem;
            }
            else if (suffix === 'lich' || suffix === 'heit') {
                word = stem;
                const tail = longestSuffix(word, ['er', 'en']);
                if (tail && inR1(tail))
                    word = word.slice(0, -tail.length);
            }
            else {
                word = stem;
                const tail = longestSuffix(word, ['lich', 'ig']);
                if (tail && inR2(tail))
                    word = word.slice(0, -tail.length);
            }
        }
    }
    return word.toLowerCase();
}
//# sourceMappingURL=stem.js.map